import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { useCreateCustomerSitesMutation, useGetCustomerQuery } from '../api/customersApi';
import { CustomerSitesForm } from '../components/CustomerSitesForm';
import { EMPTY_SITES_FORM } from '../constants/customers.constants';
import { useExistingSiteIds, useGoToCustomerSites } from '../hooks';

// Manage customers ▸ customer ▸ Add customer sites.
export const AddCustomerSitesPage = () => {
  const t = useTranslations();
  const { customerId } = useParams();

  const { data: customer, isLoading, isError } = useGetCustomerQuery(customerId);
  const [createSites, { isLoading: isSubmitting }] = useCreateCustomerSitesMutation();
  const existingIds = useExistingSiteIds(customerId);
  const goToSites = useGoToCustomerSites(customerId);

  const handleSubmit = useCallback(
    async (values) => {
      const result = await createSites({ customerId, values });
      if (result.error) return;
      goToSites();
    },
    [createSites, customerId, goToSites],
  );

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !customer) {
    return (
      <EmptyState
        title={t('CUSTOMERS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToSites}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <CustomerSitesForm
      title={t('CUSTOMER_SITES_ADD_TITLE', { customerName: customer.customerName })}
      initialValues={EMPTY_SITES_FORM}
      existingIds={existingIds}
      onSubmit={handleSubmit}
      onClose={goToSites}
      isSubmitting={isSubmitting}
    />
  );
};
